import { useEffect, useState } from 'react';
import { AI_PROVIDERS, AI_MODELS, DEFAULT_AI_PROVIDER, DEFAULT_AI_MODEL } from '@ellie/shared';
import type { AIProviderCode } from '@ellie/shared';
import { supabase } from '@/lib/supabase';
import { Loader2, Save, Settings } from 'lucide-react';

interface AIProviderSettings {
  provider: AIProviderCode;
  model: string;
  api_key_env_var: string;
}

/** Map provider code → default env var name */
const DEFAULT_ENV_VARS: Record<AIProviderCode, string> = {
  anthropic: 'ANTHROPIC_API_KEY',
  openai: 'OPENAI_API_KEY',
  gemini: 'GEMINI_API_KEY',
};

export function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [provider, setProvider] = useState<AIProviderCode>(DEFAULT_AI_PROVIDER);
  const [model, setModel] = useState(DEFAULT_AI_MODEL);
  const [apiKeyEnvVar, setApiKeyEnvVar] = useState('ANTHROPIC_API_KEY');

  // Load current settings on mount
  useEffect(() => {
    async function load() {
      const { data, error: fetchError } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'ai_provider')
        .single();

      if (!fetchError && data?.value) {
        const val = data.value as AIProviderSettings;
        if (val.provider) setProvider(val.provider);
        if (val.model) setModel(val.model);
        if (val.api_key_env_var) setApiKeyEnvVar(val.api_key_env_var);
      }

      setLoading(false);
    }

    load();
  }, []);

  // When provider changes, reset model to first available and update env var
  function handleProviderChange(newProvider: AIProviderCode) {
    setProvider(newProvider);
    const models = AI_MODELS[newProvider];
    setModel(models[0]?.id ?? '');
    setApiKeyEnvVar(DEFAULT_ENV_VARS[newProvider]);
    setSaved(false);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);

    const value: AIProviderSettings = {
      provider,
      model,
      api_key_env_var: apiKeyEnvVar,
    };

    const { error: upsertError } = await supabase
      .from('app_settings')
      .upsert(
        { key: 'ai_provider', value, updated_at: new Date().toISOString() },
        { onConflict: 'key' },
      );

    if (upsertError) {
      setError(upsertError.message);
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }

    setSaving(false);
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 size={24} className="animate-spin text-brand" />
      </div>
    );
  }

  const availableModels = AI_MODELS[provider] ?? [];

  return (
    <div className="mx-auto max-w-2xl p-6">
      <div className="mb-6 flex items-center gap-3">
        <Settings size={24} className="text-brand" />
        <h1 className="font-heading text-2xl text-text-primary">Settings</h1>
      </div>

      {/* AI Provider section */}
      <section className="rounded-lg border border-border bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-text-primary">Default AI Provider</h2>
        <p className="mb-5 text-sm text-text-muted">
          Configure which AI provider and model is used for production workbook generation. This
          does not affect Studio, where you can override the provider per generation.
        </p>

        <div className="space-y-4">
          {/* Provider */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-text-muted">
              Provider
            </label>
            <select
              value={provider}
              onChange={(e) => handleProviderChange(e.target.value as AIProviderCode)}
              className="w-full rounded-lg border border-border px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
            >
              {AI_PROVIDERS.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          {/* Model */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-text-muted">
              Model
            </label>
            <select
              value={model}
              onChange={(e) => {
                setModel(e.target.value);
                setSaved(false);
              }}
              className="w-full rounded-lg border border-border px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
            >
              {availableModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          {/* API Key Env Var (informational) */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-text-muted">
              API Key Environment Variable
            </label>
            <input
              type="text"
              value={apiKeyEnvVar}
              onChange={(e) => {
                setApiKeyEnvVar(e.target.value);
                setSaved(false);
              }}
              className="w-full rounded-lg border border-border px-3 py-2 text-sm font-mono text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
              placeholder="ANTHROPIC_API_KEY"
            />
            <p className="mt-1 text-xs text-text-light">
              The name of the environment variable that holds the API key. The actual key is set in
              the Supabase Edge Function secrets — not stored here.
            </p>
          </div>
        </div>

        {/* Save button + feedback */}
        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-dark disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Saving...' : 'Save'}
          </button>

          {saved && <span className="text-sm text-green-600">Settings saved.</span>}
          {error && <span className="text-sm text-red-600">{error}</span>}
        </div>
      </section>
    </div>
  );
}
